// src/components/album/voice/MediaVoiceNotes.jsx
import React, { useState, useRef, useEffect } from 'react';
import { Mic, Play, Pause, Pin, ThumbsUp, Trash2, Reply, MoreVertical, X, ChevronDown } from 'lucide-react';
import { albumService } from '../../../services/albumService';

export const MediaVoiceNotes = ({ 
  mediaItem, 
  voiceSettings,
  isOwner,
  canUserRecord = false,
  onRecord,
  onClose,
  bookId,      
  galleryId,   
  user  
}) => {
  const { voiceNotes = [] } = mediaItem;
  const [playingNoteId, setPlayingNoteId] = useState(null);
  const audioRefs = useRef({});

  // Cleanup effect
  useEffect(() => {
    return () => {
      // Pause and cleanup all audio elements on unmount
      Object.values(audioRefs.current).forEach(audio => {
        if (audio) {
          audio.pause();
          audio.currentTime = 0;
        }
      });
    };
  }, []);

  // Reset playing state when voice notes change
  useEffect(() => {
    setPlayingNoteId(null);
  }, [voiceNotes]);

  const pinnedNote = voiceNotes.find(note => note.isPinned);
  const otherNotes = voiceNotes.filter(note => !note.isPinned);

  const handlePlayNote = async (note) => {
    const audio = audioRefs.current[note.id];
    if (!audio) {
      console.error('Audio element not found for note:', note.id);
      return;
    }

    try {
      // Pause other audios
      Object.entries(audioRefs.current).forEach(([id, audioEl]) => {
        if (id !== note.id && audioEl) {
          audioEl.pause();
          audioEl.currentTime = 0;
        }
      });

      if (playingNoteId === note.id) {
        // Pause current audio
        audio.pause();
        setPlayingNoteId(null);
      } else {
        // Reset to start
        audio.currentTime = 0;
        
        // Ensure audio is unmuted and has volume
        audio.muted = false;
        audio.volume = 1.0;
        
        // Log audio state before play
        console.log('🔊 Audio state:', {
          volume: audio.volume,
          muted: audio.muted,
          paused: audio.paused
        });
        
        // Attempt to play
        const playPromise = audio.play();
        
        if (playPromise !== undefined) {
          await playPromise;
          setPlayingNoteId(note.id);
          console.log('✅ Playing voice note:', note.id);
        }
      }
    } catch (error) {
      console.error('❌ Audio playback failed:', error);
      
      // Handle different error types
      if (error.name === 'NotAllowedError') {
        alert('Audio playback was blocked. Please interact with the page first.');
      } else if (error.name === 'NotSupportedError') {
        alert('This audio format is not supported by your browser.');
      } else {
        alert('Failed to play voice note. Please try again.');
      }
      
      setPlayingNoteId(null);
    }
  };

  const handleDeleteNote = async (note) => {
    if (!window.confirm('Delete this voice note?')) return;
    
    try {
      await albumService.deleteMediaVoiceNote(
        bookId,
        galleryId,
        mediaItem.id,
        note.id
      );
      
      // Reload to refresh
      window.location.reload();
    } catch (error) {
      console.error('Error deleting note:', error);
      alert('Failed to delete voice note');
    }
  };

  // Check permissions
  const canDeleteNote = (note) => {
    return isOwner || (user?.uid && note.createdBy === user.uid);
  };

  const VoiceNoteCard = ({ note, isPinned = false }) => {
  const isPlaying = playingNoteId === note.id;

  return (
    <div 
      className={`bg-white rounded-lg p-4 hover:shadow-sm transition-shadow border ${
        isPinned ? 'border-amber-300 bg-gradient-to-br from-amber-50 to-orange-50' : 'border-gray-200'
      }`}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-start gap-3">
        {/* Speaker Avatar */}
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-400 to-pink-400 flex items-center justify-center flex-shrink-0 shadow-md">
          <span className="text-white text-sm font-semibold">
            {note.speaker?.name?.charAt(0) || '?'}
          </span>
        </div>

        <div className="flex-1 min-w-0">
          {/* Speaker Info */}
          <div className="flex items-center justify-between mb-2">
            <div>
              <div className="flex items-center gap-2">
                <h5 className="font-semibold text-sm text-gray-900">
                  {note.speaker?.name || 'Anonymous'}
                </h5>
                {isPinned && (
                  <Pin className="w-3 h-3 text-amber-600" />
                )}
              </div>
              {note.speaker?.relation && (
                <p className="text-xs text-gray-500">{note.speaker.relation}</p>
              )}
            </div>
            <button 
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteNote(note);
              }}
              disabled={!canDeleteNote(note)}
              className="text-gray-400 hover:text-red-600 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              title={canDeleteNote(note) ? 'Delete voice note' : 'No permission'}
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>

          {/* Transcript Preview */}
          {note.transcript && (
            <p className="text-xs text-gray-600 mb-3 line-clamp-2">
              "{note.transcript}"
            </p>
          )}

          {/* Playback Controls */}
          <div className="flex items-center gap-4">
            <button
              onClick={async (e) => {
                e.stopPropagation();
                await handlePlayNote(note);
              }}
              className="flex items-center gap-1.5 text-sm font-medium text-purple-600 hover:text-purple-700 transition-colors"
            >
              {isPlaying ? (
                <Pause className="w-4 h-4" />
              ) : (
                <Play className="w-4 h-4" />
              )}
              <span>{note.duration}s</span>
            </button>

            {/* Reactions */}
            <div className="flex items-center gap-3 ml-auto">
              <button 
                onClick={(e) => e.stopPropagation()}
                className="flex items-center gap-1 text-xs text-gray-500 hover:text-purple-600 transition-colors"
              >
                <ThumbsUp className="w-3 h-3" />
                {note.reactions?.likes || 0}
              </button>
              {voiceSettings?.mediaVoiceNotes?.allowMultipleSpeakers && (
                <button 
                  onClick={(e) => e.stopPropagation()}
                  className="flex items-center gap-1 text-xs text-gray-500 hover:text-purple-600 transition-colors"
                >
                  <Reply className="w-3 h-3" />
                  Reply
                </button>
              )}
            </div>
          </div>
          {/* REMOVED AUDIO ELEMENT FROM HERE */}
        </div>
      </div>
    </div>
  );
};

return (
  <div 
    className="bg-gradient-to-br from-purple-50 via-pink-50 to-purple-50 border-t border-purple-200"
    onClick={(e) => e.stopPropagation()}
  >
    {/* Header */}
    <div className="flex items-center justify-between p-4 border-b border-purple-200 bg-white/50">
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 rounded-lg bg-purple-600 flex items-center justify-center">
          <Mic className="w-4 h-4 text-white" />
        </div>
        <div>
          <h3 className="text-base font-semibold text-gray-900">Voice Stories</h3>
          <p className="text-xs text-gray-500">{voiceNotes.length} voice note{voiceNotes.length !== 1 ? 's' : ''}</p>
        </div>
      </div>
      <button
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-lg hover:bg-white/80"
      >
        <ChevronDown className="w-5 h-5" />
      </button>
    </div>

    {/* Voice Notes List */}
    <div className="p-4 space-y-3 max-h-96 overflow-y-auto">
      {/* Pinned Note First */}
      {pinnedNote && (
        <div className="mb-4">
          <VoiceNoteCard note={pinnedNote} isPinned={true} />
        </div>
      )}

      {/* Other Notes */}
      {otherNotes.map((note) => (
        <VoiceNoteCard key={note.id} note={note} />
      ))}

      {voiceNotes.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          <Mic className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          <p className="text-sm">No voice notes yet</p>
          <p className="text-xs mt-1">Be the first to share your story!</p>
        </div>
      )}
    </div>

    {/* Record Button */}
    {canUserRecord && onRecord && (
      <div className="p-4 border-t border-purple-200 bg-white/50">
        <button
          onClick={(e) => {
            e.stopPropagation();
            onRecord();
          }}
          className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium transition-colors"
        >
          <Mic className="w-4 h-4" />
          Record Your Voice Note
        </button>
      </div>
    )}

    {/* ALL AUDIO ELEMENTS AT ROOT LEVEL LIKE ALBUMVOICEPLAYER */}
    {voiceNotes.map((note) => (
      <audio
        key={note.id}
        ref={(el) => {
          if (el) audioRefs.current[note.id] = el;
        }}
        src={note.url}
        preload="metadata"
        onEnded={() => {
          console.log('Audio ended:', note.id);
          setPlayingNoteId(null);
        }}
        onError={(e) => {
          console.error('Audio error:', e, note.url);
          setPlayingNoteId(null);
        }}
        onLoadedMetadata={(e) => {
          console.log('Audio loaded:', note.id, 'Duration:', e.target.duration);
        }}
      />
    ))}
  </div>
);
};